import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import RfPageFrame from '../layout/RfPageFrame';

const domains = [
  {
    id: 'ai',
    icon: '◆',
    iconBg: '#E6F1FB',
    iconColor: '#185FA5',
    name: 'Artificial intelligence',
    desc: 'Senior ML engineers to AI product leads, covering foundation model work through to applied AI deployment.',
    pills: ['ML engineer', 'AI researcher', 'MLOps', 'AI product manager', 'Data scientist'],
    roles: ['Machine learning engineer', 'Applied AI scientist', 'MLOps / AI platform engineer', 'Responsible AI lead', 'AI product manager', 'NLP / computer vision specialist', 'AI safety researcher'],
    skills: ['PyTorch / TensorFlow proficiency', 'LLM fine-tuning and RLHF', 'Vector databases and RAG pipelines', 'Model governance and red-teaming', 'AI system design at scale'],
    note: "Given Rakfort's own AI security pedigree, we apply adversarial-resilience screening to every AI candidate — verifying awareness of prompt injection, model poisoning, and jailbreak attack vectors.",
    extra: null,
  },
  {
    id: 'cyber',
    icon: '⬡',
    iconBg: '#EEEDFE',
    iconColor: '#534AB7',
    name: 'Cybersecurity',
    desc: 'From SOC analysts and penetration testers to CISO-level leadership and AI security specialists.',
    pills: ['CISO', 'Pen tester', 'SOC analyst', 'GRC lead', 'AI security'],
    roles: ['Chief information security officer', 'Penetration tester (web / cloud / AI)', 'Security operations analyst (L1–L3)', 'GRC and compliance manager', 'Cloud security architect', 'AI/ML security engineer', 'Incident response lead'],
    skills: ['Offensive and defensive security tooling', 'Zero-trust architecture', 'AI firewall and LLM security', 'ISO 27001 / NIS2 / GDPR', 'Threat intelligence and SIEM'],
    note: 'All cybersecurity placements undergo enhanced vetting including SC / DV clearance compatibility checks, and we source candidates already familiar with AI-era threat landscapes.',
    extra: null,
  },
  {
    id: 'software',
    icon: '{ }',
    iconBg: '#EAF3DE',
    iconColor: '#3B6D11',
    name: 'Software engineering',
    desc: 'Full-stack, backend, platform and DevSecOps talent for product companies and enterprise engineering teams.',
    pills: ['Backend engineer', 'Full-stack', 'DevSecOps', 'Engineering manager', 'Platform'],
    roles: ['Senior / staff software engineer', 'Backend engineer (Go / Rust / Python)', 'Full-stack engineer (React / Node)', 'DevSecOps / platform engineer', 'Engineering manager', 'Site reliability engineer', 'Solutions architect'],
    skills: ['Cloud-native development (AWS / GCP / Azure)', 'Secure SDLC and shift-left practices', 'Microservices and distributed systems', 'CI/CD and infrastructure-as-code', 'Technical leadership and mentoring'],
    note: 'We prioritise candidates with a security-aware engineering mindset — teams deploying AI or operating in regulated sectors require engineers who build with compliance baked in.',
    extra: null,
  },
  {
    id: 'finance',
    icon: '£',
    iconBg: '#FAEEDA',
    iconColor: '#854F0B',
    name: 'Finance',
    desc: 'A comprehensive bench of finance professionals spanning controllership, audit, management, international operations, fintech and C-suite leadership.',
    pills: ['Finance controller', 'Auditor', 'Finance manager', 'Foreign subsidiaries', 'CFO', 'Quant', 'Compliance', 'Fintech PM'],
    roles: [
      'Chief financial officer (CFO) / VP finance',
      'Finance controller (group & divisional)',
      'Finance manager (operational & strategic)',
      'Financial controller — foreign subsidiaries',
      'Internal audit manager / chief audit executive',
      'External audit liaison & statutory reporting lead',
      'Management accountant',
      'Financial planning & analysis (FP&A) manager',
      'Quantitative analyst / researcher',
      'Risk manager (market / credit / operational)',
      'Compliance officer (FCA / CBI / MiFID II regulated)',
      'Treasury manager & cash flow specialist',
      'Tax manager (corporate & international)',
      'Fintech product manager',
      'AI in finance specialist',
      'Payroll & benefits manager',
      'Mergers & acquisitions (M&A) analyst',
      'Investor relations manager',
    ],
    skills: [
      'IFRS / GAAP financial reporting & consolidation',
      'Multi-entity & multi-currency accounting',
      'Intercompany transactions & transfer pricing',
      'Internal controls, SOX & audit readiness',
      'Regulatory compliance (FCA, CBI, MiFID II)',
      'Risk frameworks, stress testing & scenario analysis',
      'Financial modelling, budgeting & forecasting',
      'ERP systems (SAP, Oracle, NetSuite, Dynamics)',
      'Python / SQL for financial analysis & automation',
      'AI-augmented finance tools & data analytics',
    ],
    note: 'We screen for regulatory standing with the FCA and Central Bank of Ireland, and can identify candidates with experience deploying AI in trading, fraud detection and AML programmes.',
    extra: {
      groups: [
        { label: 'Controllership & accounting', tags: ['Finance controller', 'Group controller', 'Divisional controller', 'Management accountant', 'AP/AR manager', 'Payroll manager'] },
        { label: 'Audit & assurance', tags: ['Internal audit manager', 'Chief audit executive', 'External audit liaison', 'SOX compliance lead', 'IT audit specialist'] },
        { label: 'International & subsidiaries', tags: ['Foreign subsidiaries controller', 'Transfer pricing manager', 'International tax manager', 'Cross-border consolidation lead', 'Intercompany accounting specialist'] },
        { label: 'Financial planning & analysis', tags: ['FP&A manager', 'Budgeting & forecasting lead', 'M&A analyst', 'Investor relations manager', 'Commercial finance manager'] },
        { label: 'Risk, compliance & treasury', tags: ['Risk manager', 'Compliance officer', 'Treasury manager', 'Credit risk analyst', 'Regulatory reporting lead'] },
        { label: 'Executive & specialist', tags: ['CFO', 'VP finance', 'Quant analyst', 'Fintech PM', 'AI finance specialist', 'Tax manager'] },
      ],
    },
  },
  {
    id: 'construction',
    icon: '□',
    iconBg: '#E1F5EE',
    iconColor: '#0F6E56',
    name: 'Construction',
    desc: 'Project managers, site engineers, quantity surveyors and smart-building technologists for modern construction firms.',
    pills: ['Project manager', 'QS', 'BIM lead', 'Site engineer', 'Smart building'],
    roles: ['Project manager / programme director', 'Quantity surveyor (pre / post contract)', 'Site manager and engineer', 'BIM / digital construction lead', 'M&E engineer', 'HSE manager', 'Smart building / PropTech specialist'],
    skills: ['Contract management (JCT / NEC)', 'BIM Level 2 / 3 and Revit', 'CDM and HSE compliance', 'Cost planning and value engineering', 'Digital twins and IoT integration'],
    note: 'We place professionals with experience on technology-enabled build programmes — from data centre construction (relevant to AI infrastructure clients) through to smart commercial builds.',
    extra: null,
  },
];

const stats = [
  { num: '5', label: 'specialist domains' },
  { num: '48h', label: 'avg. first shortlist' },
  { num: '92%', label: 'placement retention' },
  { num: 'EU&UK', label: 'coverage' },
];

const processSteps = [
  { num: '01', name: 'Brief', detail: 'Role scoping, culture, seniority, compliance needs' },
  { num: '02', name: 'Search', detail: 'Active + passive candidate mapping across domains' },
  { num: '03', name: 'Screen', detail: 'Technical interviews, background & security vetting' },
  { num: '04', name: 'Shortlist', detail: 'Ranked profiles with evidence packs delivered to you' },
  { num: '05', name: 'Place', detail: 'Offer management, onboarding support, 90-day guarantee' },
];

export default function ServicesHumanResources() {
  const [activeId, setActiveId] = useState(null);
  const activeDomain = domains.find(d => d.id === activeId);

  function toggleDomain(id) {
    setActiveId(prev => (prev === id ? null : id));
  }

  return (
    <>
      {/* Hero */}
      <div className="rf-hero-band">
        <RfPageFrame className="!py-10 md:!py-14">
          <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#5c6d7e] mb-2">
            Rakfort Limited — Recruitment Services
          </p>
          <h1 className="text-2xl md:text-[2.25rem] font-semibold text-[#2c2c2c] tracking-tight max-w-3xl leading-tight">
            Specialist talent acquisition for high-stakes industries
          </h1>
          <p className="mt-4 text-[1.05rem] text-[#5c5c5c] max-w-2xl leading-relaxed">
            We identify, vet, and place professionals across AI, cybersecurity, software engineering, finance, and construction — combining deep domain expertise with a rigorous, security-conscious screening process.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            {['Permanent placement', 'Contract & interim', 'Executive search', 'Team build-out', 'Diversity hiring'].map(b => (
              <span key={b} className="text-xs font-medium px-3 py-1 rounded-full bg-white border border-[#e5eef6] text-[#5c5c5c]">{b}</span>
            ))}
          </div>
        </RfPageFrame>
      </div>

      {/* Stats */}
      <section className="bg-white border-b border-[#e5eef6]">
        <RfPageFrame className="py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map(s => (
              <div key={s.num} className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-[#2c2c2c] leading-none mb-1">{s.num}</div>
                <div className="text-xs text-[#5c6d7e]">{s.label}</div>
              </div>
            ))}
          </div>
        </RfPageFrame>
      </section>

      {/* Domain cards */}
      <section className="bg-[#f0f7ff] border-b border-[#e5eef6]">
        <RfPageFrame className="py-16">
          <p className="text-xs font-medium uppercase tracking-[0.1em] text-[#5c6d7e] mb-4">Select a domain to explore</p>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3 mb-4">
            {domains.map(d => (
              <div
                key={d.id}
                onClick={() => toggleDomain(d.id)}
                className={`rf-card p-5 cursor-pointer bg-white space-y-3 ${activeId === d.id ? '!border-[#005a9c] !border' : ''}`}
              >
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center text-base font-mono"
                  style={{ background: d.iconBg, color: d.iconColor }}
                >
                  {d.icon}
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#2c2c2c] mb-1">{d.name}</div>
                  <div className="text-xs text-[#5c5c5c] leading-relaxed">{d.desc}</div>
                </div>
                <div className="flex flex-wrap gap-1">
                  {d.pills.map(p => (
                    <span key={p} className="text-[10px] px-2 py-0.5 rounded-full bg-[#f0f7ff] border border-[#e5eef6] text-[#5c5c5c]">{p}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Detail panel */}
          {activeDomain && (
            <div className="rf-card bg-white p-6 mt-2">
              {/* Header */}
              <div className="flex items-start gap-3 mb-5 pb-4 border-b border-[#e5eef6]">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center text-base font-mono flex-shrink-0"
                  style={{ background: activeDomain.iconBg, color: activeDomain.iconColor }}
                >
                  {activeDomain.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-base font-semibold text-[#2c2c2c]">{activeDomain.name} — roles & requirements</div>
                  <div className="text-xs text-[#5c6d7e] mt-1 leading-relaxed">{activeDomain.note}</div>
                </div>
                <button
                  onClick={() => setActiveId(null)}
                  className="text-xs text-[#5c6d7e] hover:text-[#2c2c2c] px-2 py-1 rounded hover:bg-[#f0f7ff] transition-colors flex-shrink-0"
                >
                  close ×
                </button>
              </div>

              {/* Roles & Skills */}
              <div className="grid md:grid-cols-2 gap-6 mb-4">
                <div>
                  <p className="text-[10px] font-medium uppercase tracking-[0.08em] text-[#5c6d7e] mb-3">Roles we place</p>
                  <ul className="space-y-1.5">
                    {activeDomain.roles.map(r => (
                      <li key={r} className="flex items-start gap-2 text-sm text-[#5c5c5c] leading-relaxed">
                        <span className="w-1 h-1 rounded-full bg-[#5c6d7e] flex-shrink-0 mt-2" />
                        {r}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-[10px] font-medium uppercase tracking-[0.08em] text-[#5c6d7e] mb-3">Skills & expertise we screen for</p>
                  <ul className="space-y-1.5">
                    {activeDomain.skills.map(s => (
                      <li key={s} className="flex items-start gap-2 text-sm text-[#5c5c5c] leading-relaxed">
                        <span className="w-1 h-1 rounded-full bg-[#5c6d7e] flex-shrink-0 mt-2" />
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Finance specialisms */}
              {activeDomain.extra?.groups && (
                <div className="mt-4 pt-4 border-t border-[#e5eef6]">
                  <p className="text-[10px] font-medium uppercase tracking-[0.08em] text-[#005a9c] mb-3">Finance specialisms at a glance</p>
                  <div className="space-y-3">
                    {activeDomain.extra.groups.map(g => (
                      <div key={g.label}>
                        <p className="text-[10px] text-[#5c6d7e] mb-1.5">{g.label}</p>
                        <div className="flex flex-wrap gap-1.5">
                          {g.tags.map(t => (
                            <span key={t} className="text-xs px-2.5 py-1 rounded-full bg-[#f0f7ff] border border-[#e5eef6] text-[#5c5c5c]">{t}</span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </RfPageFrame>
      </section>

      {/* Process */}
      <section className="bg-white border-b border-[#e5eef6]">
        <RfPageFrame className="py-16">
          <p className="text-xs font-medium uppercase tracking-[0.1em] text-[#5c6d7e] mb-6">Our process</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {processSteps.map(s => (
              <div key={s.num} className="rf-card p-4 text-center bg-white">
                <div className="text-xl font-bold text-[#005a9c] leading-none mb-1">{s.num}</div>
                <div className="text-xs font-semibold text-[#2c2c2c] mb-1">{s.name}</div>
                <div className="text-[10px] text-[#5c6d7e] leading-snug">{s.detail}</div>
              </div>
            ))}
          </div>
        </RfPageFrame>
      </section>

      {/* CTA */}
      <section className="bg-[#f0f7ff] border-b border-[#e5eef6]">
        <RfPageFrame className="py-16">
          <p className="text-xs font-medium uppercase tracking-[0.1em] text-[#5c6d7e] mb-4">Get in touch</p>
          <div className="flex flex-wrap gap-3">
            <Link to="/contactus" className="inline-flex items-center gap-1.5 text-sm font-medium px-5 py-2.5 rounded border border-[#005a9c] text-[#005a9c] hover:bg-[#005a9c]/5 transition-colors">
              Submit a hiring brief <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <Link to="/contactus" className="inline-flex items-center gap-1.5 text-sm font-medium px-5 py-2.5 rounded border border-[#e5eef6] bg-white text-[#2c2c2c] hover:border-[#005a9c]/30 transition-colors">
              View engagement models <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <Link to="/contactus" className="inline-flex items-center gap-1.5 text-sm font-medium px-5 py-2.5 rounded border border-[#e5eef6] bg-white text-[#2c2c2c] hover:border-[#005a9c]/30 transition-colors">
              Why Rakfort <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </RfPageFrame>
      </section>
    </>
  );
}
